import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';
import WarningIcon from '@mui/icons-material/Warning';
import ToastMessage from '@/components/toasts/ToastMessage';
import { Done } from '@mui/icons-material';

export default function ShipmentForm({ data }) {
  const { config } = useRouteLoaderData('auth');

  const [selected, setSelected] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [types, setTypes] = useState([]);
  const [checked, setChecked] = useState(false);
  const [transferErrors, setTransferErrors] = useState({});
  const [transfered, setTransfered] = useState({});
  const [existingSerialNumbers, setExistingSerialNumbers] = useState({});

  useEffect(() => {
    const items =
      data?.orderItems || data?.shipmentItems || data?.salesInvoiceItems;
    const fullItems = items
      .map((i) => {
        if (!i.picks || i.picks.length === 0) return;
        return i.picks
          .map((pick) => {
            return pick.serialNumbers.map((sn) => ({
              id: sn,
              title: i.title,
              description: i.description,
              articleNumber: i.articleNumber,
              serialNumber: sn,
              selected: true,
              type: '',
              articleId: i.articleId,
            }));
          })
          .flat();
      })
      .flat()
      .filter((i) => i);

    setSelected(fullItems);

    const customerNumber = data.recipientCustomerNumber ?? data?.customerNumber;

    (async () => {
      const toastId = toast.loading(
        'Existierende Seriennummern in Tanss werden geladen..',
      );
      const { data: companies } = await proxy.put(
        `/${config.warehouse.tanssInterface}/companies`,
        {},
      );
      const companyId =
        companies?.content.find((c) => c.displayId === customerNumber)?.id ??
        null;
      const { data: manufacturers } = await proxy.get(
        `/${config.warehouse.tanssInterface}/manufacturers`,
      );

      const { data: types } = await proxy.get(
        `/${config.warehouse.tanssInterface}/peripheries/types`,
      );
      const { data: pcs } = await proxy.put(
        `/${config.warehouse.tanssInterface}/pcs`,
        {
          companyId: companyId,
          active: 'ACTIVE_ONLY',
        },
      );
      const { data: components } = await proxy.put(
        `/${config.warehouse.tanssInterface}/components`,
        {
          companyId: companyId,
          active: 'ACTIVE_ONLY',
        },
      );
      const { data: peripheries } = await proxy.put(
        `/${config.warehouse.tanssInterface}/peripheries`,
        {
          companyId: companyId,
          active: 'ACTIVE_ONLY',
        },
      );

      toast.update(toastId, {
        render: 'Seriennummern geladen',
        type: 'success',
        isLoading: false,
        autoClose: 1000,
      });

      //console.log("tanssSachen", companies?.content, manufacturers?.content, types?.content, pcs?.content, components?.content, peripheries?.content);

      setCompanies(companies?.content);
      setManufacturers(manufacturers?.content);
      setTypes(types?.content);
      const pcSNs = pcs?.content.map((pc) => {
        return { serialNumber: pc.serialNumber };
      });

      // Set the correct company based on customerNumber
      const foundCompany = companies?.content.find(
        (c) => c.displayId === customerNumber,
      );
      if (foundCompany) {
        setCompany(foundCompany);
      }

      // Check for existing serialnumbers
      const existingSerialNumberMap = {};

      fullItems.forEach((item) => {
        let existingSerialNumber = false;
        if (item.type == '' || item.type === 'pc' || item.type === 'server') {
          existingSerialNumber = !!pcSNs.find(
            (pc) => item.serialNumber == pc.serialNumber,
          );
        }
        if (item.type == '' || item.type === 'component') {
          existingSerialNumber =
            existingSerialNumber ||
            !!components?.content?.find(
              (component) => item.serialNumber == component.serialNumber,
            );
        }

        // Check peripheries for empty types and all non-pc/server/component types
        if (
          item.type == '' ||
          (item.type != 'pc' &&
            item.type != 'server' &&
            item.type != 'component')
        ) {
          existingSerialNumber =
            existingSerialNumber ||
            !!peripheries?.content?.find(
              (periphery) => item.serialNumber == periphery.serialNumber,
            );
        }

        if (existingSerialNumber) {
          existingSerialNumberMap[item.id] = true;
        }
      });

      setExistingSerialNumbers(existingSerialNumberMap);
    })();
  }, [data]);

  useEffect(() => {
    if (selected.filter((i) => i.selected).length === selected.length) {
      setChecked(true);
    } else {
      setChecked(false);
    }
  }, [selected]);

  function selectAll(boolValue) {
    setChecked(boolValue);
    const updatedSelection = selected?.map((item) => {
      item.selected = boolValue;
      return item;
    });
    setSelected(updatedSelection);
  }

  async function transfer(force = false) {
    if (!company) {
      toast.error('Kein Kunde ausgewählt');
      return;
    }

    const alreadyExistingSerialNumberItems = [];

    if (!force) {
      selected.forEach((item) => {
        if (item.selected && existingSerialNumbers[item.id])
          alreadyExistingSerialNumberItems.push(item.title);
      });
    }

    if (alreadyExistingSerialNumberItems.length > 0) {
      const itemNameString = alreadyExistingSerialNumberItems.join(', ');

      let forceNow = await new Promise((resolve) => {
        const toastId = toast.warning(
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ToastMessage
              width="80%"
              title={`Für ${itemNameString} ist bereits eine Seriennummer in Tanss vorhanden. Wollen Sie es wirklich übertragen?`}
            />
            <div style={{ fisplay: 'flex', flexDirection: 'row' }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ width: '120px', height: '20px', bottom: '0px' }}
                onClick={() => {
                  toast.dismiss(toastId);
                  resolve(true);
                }}
              >
                Übertragen
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  /* width: '20px', */ height: '20px',
                  bottom: '0px',
                  marginLeft: '10px',
                }}
                onClick={() => {
                  toast.dismiss(toastId);
                  resolve(false);
                }}
              >
                Nein
              </Button>
            </div>
          </div>,
          {
            autoClose: false,
            closeButton: true,
          },
        );
      });

      if (forceNow) {
        transfer(true);
      }
      return;
    }

    const results = await Promise.all(
      selected.map(async (item) => {
        if (!item.selected) return null;

        try {
          const { data: article } = await proxy.get(
            `${config.warehouse.weclappInterface}/article/id/${item.articleId}`,
          );

          const manufacturerId = manufacturers.find(
            (m) => m.name === article.manufacturerName,
          )?.id;

          let model =
            item.title.length > 49
              ? item.title.substr(0, 49).split(' ').slice(0, -1).join(' ')
              : item.title;

          const device = {
            active: true,
            companyId: company.id,
            serialNumber: item.serialNumber,
            articleNumber: item.articleNumber,
            manufacturerNumber: item.articleNumber,
            ownageType: 'FOREIGN',
            manufacturerId,
            date: Date.now() / 1000,
            guarantee: {
              remark: data.salesOrderNumber,
            },
          };

          if (item.type === 'pc') {
            await proxy.post(`/${config.warehouse.tanssInterface}/pcs`, {
              ...device,
              model,
            });
          } else if (item.type === 'server') {
            await proxy.post(`/${config.warehouse.tanssInterface}/pcs`, {
              ...device,
              model,
              server: true,
            });
          } else if (item.type === 'component') {
            await proxy.post(`/${config.warehouse.tanssInterface}/components`, {
              ...device,
              type: model,
            });
          } else {
            await proxy.post(
              `/${config.warehouse.tanssInterface}/peripheries`,
              {
                ...device,
                type: model,
                peripheryTypeId: item.type,
              },
            );
          }

          return {
            id: item.id,
            error: null,
          };
        } catch (e) {
          return {
            id: item.id,
            error: {
              message:
                e?.response?.data?.message ||
                e?.response?.statusText ||
                e.message ||
                'Unbekannter Fehler',
              status: e?.response?.status,
              data: e?.response?.data,
            },
          };
        }
      }),
    );

    const errorMap = {};
    const transfers = {};
    results.forEach((r) => {
      if (r?.error) {
        errorMap[r?.id] = r.error;
      } else {
        transfers[r?.id] = true;
      }
    });

    setTransferErrors(errorMap);
    setTransfered(transfers);

    if (Object.keys(errorMap).length > 0) {
      toast.error(
        `Übertragung von ${Object.keys(errorMap).length} Artikeln fehlgeschlagen`,
      );
    } else {
      toast.success('Übertragung erfolgreich');
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
      }}
    >
      <Typography variant="h4" gutterBottom>
        {data.shipmentNumber && 'Lieferung'}
        {data.orderNumber && 'Auftrag'}
        {data.invoiceNumber && 'Rechnung'}{' '}
        {data.shipmentNumber || data.orderNumber || data.invoiceNumber}
      </Typography>
      <Autocomplete
        options={companies}
        getOptionLabel={(option) => `[${option.displayId}] ${option.name}`}
        value={company}
        onChange={(evt, value) => setCompany(value)}
        isOptionEqualToValue={(option, value) =>
          option.displayId === value.displayId
        }
        fullWidth
        renderInput={(params) => <TextField {...params} label="Kunde" />}
        sx={{ my: 2 }}
      />
      <FormControlLabel
        label="Alle auswählen"
        sx={{ ml: 2 }}
        control={
          <Checkbox
            checked={checked}
            sx={{ mr: 2 }}
            onChange={(evt) => {
              selectAll(evt.target.checked);
            }}
          />
        }
      />
      <List sx={{ width: '100%', overflowY: 'auto' }}>
        {selected?.map((item, index) => (
          <ListItem key={item.id} sx={{ width: '100%' }}>
            <Checkbox
              sx={{ mr: 2 }}
              checked={item.selected}
              onChange={(evt) => {
                setChecked(false);
                if (evt.target.checked) {
                  const p = [...selected];
                  const index = p.findIndex((i) => i.id === item.id);
                  p[index].selected = true;
                  setSelected(p);
                } else {
                  const p = [...selected];
                  const index = p.findIndex((i) => i.id === item.id);
                  p[index].selected = false;
                  setSelected(p);
                }
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <ListItemText
              primary={item.title}
              secondary={`${item.articleNumber}, SN: ${item.serialNumber}`}
              sx={{
                '& .MuiTypography-root': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
            <Box
              sx={{ width: 52, mr: 1, display: 'flex', alignItems: 'center' }}
            >
              {existingSerialNumbers[item.id] && (
                <Tooltip title={`Seriennummer existiert bereits in Tanss.`}>
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                </Tooltip>
              )}
              {transferErrors[item.id] && (
                <Tooltip
                  title={`Fehlgeschlagene Übertragung: ${transferErrors[item.id].message}`}
                >
                  <WarningIcon color="error" sx={{ mr: 1 }} />
                </Tooltip>
              )}
              {transfered[item.id] && (
                <Tooltip title={`Erfolgreich Übertragen`}>
                  <Done color="success" sx={{ mr: 1 }} />
                </Tooltip>
              )}
            </Box>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="item-type">Typ</InputLabel>
              <Select
                labelId="item-type"
                id="item-type"
                value={item.type}
                label="Typ"
                onChange={(evt) => {
                  const articleNumber = selected[index].articleNumber;
                  setSelected(
                    selected.map((item) =>
                      item.articleNumber === articleNumber
                        ? { ...item, type: evt.target.value }
                        : item,
                    ),
                  );
                }}
              >
                <MenuItem value="pc">PC</MenuItem>
                <MenuItem value="server">Server</MenuItem>
                <MenuItem value="component">Komponente</MenuItem>
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        fullWidth
        sx={{ mt: 3 }}
        onClick={() => transfer()}
      >
        Übertragen
      </Button>
    </Box>
  );
}
